import moment from 'moment';
import momentTz from 'moment-timezone';
import _ from 'lodash';
import isEqual from 'date-fns/is_equal';
import format from 'date-fns/format';
import {
  PRIORITY_PERMISSION,
  CONNECT_GA_STATUS,
  VISUAL_STATUS,
  GA_MINIMUM_RECENT_HOURS,
  ADTYPE_CHART_ID,
  newAdUsersKey,
  STATION_CHART_ID,
  ADVISIT_CHART_ID,
  TRACKING_SOURCE_NAME,
  GBQ_MINIMUM_EXPIRATION_HOURS,
  GBQ_MAXIMUM_RECENT_HOURS,
  INACTIVE_THRESHOLD_DAYS,
  gbqProjectExpirationStatus,
  GA_MAXIMUM_RECENT_HOURS,
  WEB_LOG_ACTIVE_RECENT_HOURS
} from './constant';
import { DEFAULT_TIME_ZONE_KEY } from 'components/TimeZoneSelector/constants';
import {
  ADTYPE_KEY,
  DATA_TYPE,
  STATION_TYPE_KEY,
  TYPE_KEY,
  defaultChartSelector
} from './defaultInfo';
import { getTrackingDataSourceName, isPixelAdvertiser } from './common';


export const addZero = (i) => {
  if (i < 10) {
    return '0' + i;
  }
  return i;
};

export const normalizeTimeRange = (airtime, collected_date) => {
  const MIN_DURATION_MS = 2 * 60 * 1000;

  const start = new Date(airtime);
  const end = new Date(collected_date);

  const diff = end - start;

  if (diff >= MIN_DURATION_MS) {
    return {
      airtime: start.toISOString(),
      collected_date: end.toISOString()
    };
  }

  const missing = MIN_DURATION_MS - diff;

  const half = missing / 2;

  const newStart = new Date(start.getTime() - half);
  const newEnd = new Date(end.getTime() + half);

  return {
    airtime: newStart.toISOString(),
    collected_date: newEnd.toISOString()
  };
}

export const getHigerPriorityPermission = (value, originalValue) => {
  return (PRIORITY_PERMISSION.indexOf(value) > PRIORITY_PERMISSION.indexOf(originalValue)) ? value : originalValue;
};

export const convertdate = (dateParam, isStartDate) => {
  const date = new Date(dateParam);
  const YYYY = date.getFullYear();
  const MM = addZero(date.getMonth() + 1);
  const DD = addZero(date.getDate());
  return isStartDate ?
    `${YYYY}-${MM}-${DD} 00:00:00` :
    `${YYYY}-${MM}-${DD} 23:59:59`;
};

export const convertdateUTC = (dateParam, isStartDate) => {
  // Get local date
  const localDateString = convertdate(dateParam, isStartDate);
  const localdate = new Date(localDateString);

  // Convert date to UTC
  const timeZoneOffsetMilliseconds = localdate.getTimezoneOffset() * 60000;
  const utcDate = new Date(
    localdate.getTime() + timeZoneOffsetMilliseconds
  );

  return moment(utcDate).format('YYYY-MM-DD HH:mm:ss');
};

export const convertDateStringToGraphQLFormat = (dateString) => {
  if (!dateString) {
    return '';
  }

  // We want to convert the string format: "2020-12-05 00:00:00" to "2020-12-05T00:00:00.000Z"
  // dateString usually comes from convertDate() or convertTimezoneDate() in this file.
  const formattedString = dateString.replace(' ', 'T');
  const QUERY_TIME_SUFFIX = '.000Z'

  return formattedString.includes(QUERY_TIME_SUFFIX) ? formattedString : `${formattedString}${QUERY_TIME_SUFFIX}`;
};

export const convertTimezoneDate = (dateParam, timezone, isStartDate) => {
  const convertedDate = moment.tz(dateParam, timezone);
  const dateInfo = _.get(convertedDate, '_a');

  if (!dateInfo) {
    return convertdate(dateParam, isStartDate);
  }

  const year = _.get(dateInfo, '[0]');
  const month = addZero(_.get(dateInfo, '[1]') + 1);
  const day = addZero(_.get(dateInfo, '[2]'));
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return convertdate(dateParam, isStartDate);
  }
  return isStartDate ?
    `${year}-${month}-${day} 00:00:00` :
    `${year}-${month}-${day} 23:59:59`;
};

export const compare2Date = (date1, date2) => {
  return _.isEqual(formatUtcTime(date1), formatUtcTime(date2));
};

export const convertCalendar = (selected) => {
  if (!selected) {
    return;
  }
  const { start, end } = selected;
  const startDate = convertdate(start, true);
  const endDate = convertdate(end, false);
  return `${startDate} - ${endDate}`;
};

export const convertCalendarWithTimezone = (selected, timezone) => {
  if (!selected) {
    return;
  }
  const { start, end } = selected;
  const startDate = convertTimezoneDate(start, timezone, true);
  const endDate = convertTimezoneDate(end, timezone, false);
  return `${startDate} - ${endDate}`;
};

export const range2str = (selected) => {
  let start = _.get(selected, 'start');
  let end = _.get(selected, 'end');

  if (!start || !end) {
    return;
  }

  start = new Date(start);
  end = new Date(end);
  const startDate = `${addZero(start.getMonth() + 1)}/${addZero(start.getDate())}/${start.getFullYear()}`;
  const endDate = `${addZero(end.getMonth() + 1)}/${addZero(end.getDate())}/${end.getFullYear()}`;
  return `${startDate} - ${endDate}`;
};

export const campaignRangeToString = (selected) => {
  const selectedStart = _.get(selected, 'start');
  const selectedEnd = _.get(selected, 'end');

  if (!selectedStart || !selectedEnd) {
    return;
  }

  /* There are two different ways the selected start and end times are coming through.
   *
   *  1. By default when the dates are loaded, we get the selected times as strings. They can directly
   *     be converted into moment since they are already formatted with the selected timezones.
   *  2. When we change dates in the custom calendar, we get a Date object. We need to call parseZone()
   *     to prevent converting the date into user localtime. This was causing the dates to get shifted by one day.
   *
   */

  let start = selectedStart;
  if (moment.isDate(selectedStart)) {
    start = moment.parseZone(selectedStart).format();
  }

  let end = selectedEnd;
  if (moment.isDate(selectedEnd)) {
    end = moment.parseZone(selectedEnd).format();
  }

  const startInfoArray = _.get(moment(start), '_a');
  const endInfoArray = _.get(moment(end), '_a')

  const startDate = `${addZero(_.get(startInfoArray, '[1]') + 1)}/${addZero(_.get(startInfoArray, '[2]'))}/${_.get(startInfoArray, '[0]')}`;
  const endDate = `${addZero(_.get(endInfoArray, '[1]') + 1)}/${addZero(_.get(endInfoArray, '[2]'))}/${_.get(endInfoArray, '[0]')}`;
  return `${startDate} - ${endDate}`;
};

export const formatUtcTime = (utcTime) => {
  if (_.isNil(utcTime)) {
    return 'N/A';
  }
  return moment.utc(utcTime).format('MM/DD/YYYY hh:mm:ss A');
};

export const formatStationLocalTime = (time, stationTimezone) => {
  if (_.isNil(time)) {
    return 'N/A';
  }
  return moment(time * 1000).tz(stationTimezone).format('MM/DD/YYYY hh:mm:ss A');
};

export const formatUtcToStationLocalDateTime = (timeUTC, stationTimezone) => {
  if(_.isNil(timeUTC)){
    return 'N/A';
  }
  return moment.utc(timeUTC).tz(stationTimezone).format('MM/DD/YYYY hh:mm:ss A');
};

export const formatUtcLocalTime = (utcTime) => {
  if (_.isNil(utcTime)) {
    return 'N/A';
  }
  return moment.utc(utcTime).local().format('MM/DD/YYYY hh:mm:ss A');
};

export const formatUtcTimeDate = (utcTime) => {
  if (_.isNil(utcTime)) {
    return 'N/A';
  }
  return moment.utc(utcTime).format('MM/DD/YYYY');
};

export const formatUtcLocalTimeDate = (utcTime) => {
  if (!utcTime) {
    return 'N/A';
  }
  return moment.utc(utcTime).local().format('MM/DD/YYYY');
};

export const numberWithComma = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const getLocationTime = (time) => {
  const date = new Date().toString();
  const startElement = date.indexOf('(') + 1;
  const endElement = date.indexOf(')');
  return 'Viewing in ' + date.substring(startElement, endElement);
};

export const convertToMinute = (time) => {
  let minute = Math.floor(time / 60);
  let second = Math.round(time - minute * 60);
  minute = minute < 10 ? '0' + minute : minute;
  second = second < 10 ? '0' + second : second;
  return `${minute}:${second}`;
};

export function formatDateTimeCommon({
  dateTime,
  timezoneKey,
  dateTimeFormat = 'MM/DD/YYYY HH:mm:ss',
}) {
  if (!dateTime || !moment(dateTime).isValid()) {
    return '';
  }
  if (timezoneKey) {
    return moment.tz(dateTime, timezoneKey).format(dateTimeFormat);
  }
  return moment(dateTime).format(dateTimeFormat);
}

export const formatDateToday = (utcTime) => {
  if (_.isNil(utcTime)) {
    return 'N/A';
  }
  const today = format(new Date(), 'MM/DD/YYYY');
  const utcDay = format(utcTime, 'MM/DD/YYYY');
  return isEqual(today, utcDay) ? 'Today' : utcDay;
};

export const formatDateTodayExcludingLeadingZeros = (utcTime, reportTimezone) => {
  if (_.isNil(utcTime)) {
    return 'N/A';
  }

  if (reportTimezone) {
    return moment(utcTime).tz(reportTimezone).format('M/D/YYYY');
  }

  return moment.utc(utcTime).format('M/D/YYYY');
};

export const getNextTime = (time) => {
  const hour = parseInt(time.split(':')[0]);
  const minute = parseInt(time.split(':')[1]);
  const newHour = hour + 1 === 24 ? '00' : hour + 1;
  const minuteRender = `0${minute}`.slice(-2);
  const hourRender = `0${newHour}`.slice(-2);
  return `${hourRender}:${minuteRender}`;
};

export const getNextTimeStart = (time) => {
  let hour = parseInt(time.split(':')[0]);
  let minute = parseInt(time.split(':')[1]);
  const minuteRender = `0${minute}`.slice(-2);
  const hourRender = `0${hour}`.slice(-2);
  return `${hourRender}:${minuteRender}`;
};

export const isValidTime = (indexOfCheck, newListRules) => {
  let currentTime = newListRules[indexOfCheck];
  if (currentTime.start >= currentTime.end) {
    return false;
  }
  let isValid = true;
  newListRules.map((rule, index) => {
    if (index !== indexOfCheck) {
      if (!((currentTime.start <= rule.start && currentTime.end <= rule.start) || (currentTime.start >= rule.end && currentTime.end >= rule.end))) {
        isValid = false;
      }
    }
    return rule;
  });
  return isValid;
};

export const isCompareDate = rule => {
  const { start, end } = rule;
  const startNumber = parseInt(start.split(':').join(''));
  const endNumber = parseInt(end.split(':').join(''));
  if (endNumber > startNumber) {
    return true;
  }
  return false;
};

export const splitText = value => {
  if (value.length > 0 && value.length <= 20) {
    return value;
  }
  return `${value.substring(0, 10)}...${value.substring(
    value.length - 10,
    value.length
  )}`;
};

export const getText = (value, maxLength) => {
  if (!value) {
    return '';
  }
  if (value.length > maxLength) {
    return `${value.substring(0, maxLength - 3)}...`;
  }
  return value;
};

export const checkRole = (roles, id) => {
  const roleResult = roles.filter(role => role.id === id);
  return roleResult.length > 0;
};

export const getChartDataForMap = (dataDisplay) => {

  if (dataDisplay && dataDisplay.length) {
    const regionLevel = ['country', 'province', 'county', 'city'];
    const dataLastChild = _.get(dataDisplay[dataDisplay.length - 1], 'children', []);
    const updateDataLastChild = dataLastChild && dataLastChild.filter(datum => datum.criterial_id && datum.name);
    const locationPath = dataDisplay.reduce((path, node, index) => {
      const { criterial_id, name, shortName, level } = node;
      if (index > 0 && index < dataDisplay.length) {
        const currentLevel = regionLevel[level];
        const region = {};
        region.name = name;
        region.id = criterial_id.toString();
        region.shortName = shortName;
        path[currentLevel] = region;
      }
      return path;
    }, {});
    return updateDataLastChild.map(datum => {
      const currentLevel = regionLevel[datum.level];
      return {
        ...locationPath,
        [currentLevel]: {
          id: datum.criterial_id.toString(),
          name: datum.name,
          shortName: datum.shortName
        },
        value: datum.visits
      };
    });
  }
  ;
  return [];
};

export function dateToString(date) {
  return moment(date).format('MM/DD/YYYY');
}

export const getNumberDifferenceDays = (selected) => {
  if (!selected) {
    return 0;
  }
  const start = _.get(selected, 'start', new Date());
  const end = _.get(selected, 'end', new Date());
  const endDate = moment.utc(start).startOf('day');
  const startDate = moment.utc(end).startOf('day');
  return Math.abs(endDate.diff(startDate, 'days')) + 1;
};

export const dateToDateString = (date) => {
  if (_.isNil(date)) {
    return 'N/A';
  }

  const dateObject = new Date(date);
  const localDate = moment.utc(dateObject).local();
  const today = moment();
  const yesterday = moment().subtract(1, 'days');
  if (localDate.isSame(today, 'day')) {
    return 'Today';
  }
  if (localDate.isSame(yesterday, 'day')) {
    return 'Yesterday';
  }
  return localDate.format('MMM D, YYYY');
};

export const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
    }
    return value;
  };
};

/** transformNumberToSystemUnits
 * Transforms a number to International System Units: m (millions) > k (thousands) > non-units (hundreds).
 * @param value - the number to be transformed into System Units.
 * @returns {string} - the formatted number with the System Units Suffix if applicable.
 */
export const transformNumberToSystemUnits = (value) => {
  if (!_.isNumber(value)) {
    return '--';
  }

  const MEGA_SUFFIX = 'm';
  const KILO_SUFFIX = 'k';

  // NOTE: 999,949 is limit number in the thousands before it gets rounded up to one million.
  // Implementation might change depending on costumer service feedback, but we are leaving it as is for now.
  if (value >= 999950) {
    return `${(value / 1000000).toFixed(1).replace(/\.0$/, '')}${MEGA_SUFFIX}`;
  }
  if (value >= 1000 && value <= 999949) {
    return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}${KILO_SUFFIX}`;
  }

  return value.toFixed(1).replace(/\.0$/, '');
};


export const getChartDataKey = (data) => {
  const { range, selectedAdSources } = data;

  if (Object.keys(data).length === 0) {
    return '';
  }

  const keyArr = [];
  const joinString = ' - ';

  //process range
  if ( range ) {
    const rangeConcatItem = ' - ';
    const { start, end, timezone = DEFAULT_TIME_ZONE_KEY } = range;
    const startDate = start || new Date();
    const endDate = end || new Date();
    const startDateString = convertTimezoneDate(startDate, timezone, true);
    const endDateString = convertTimezoneDate(endDate, timezone, false);
    const rangeString = `${startDateString}${rangeConcatItem}${endDateString}`;
    keyArr.push(rangeString);
  }

  //processs selectedAdSources
  if (selectedAdSources && selectedAdSources.length > 0 ) {
    const adSourceConcatItem = '_';
    const adSourceString = selectedAdSources.sort().join(adSourceConcatItem);
    keyArr.push(adSourceString);
  }

  const key = keyArr.join(joinString);
  return key;
}

export const getChartData = (campaign, customRange) => {
  if (!campaign) {
    return {};
  }
  const range = customRange || _.get(campaign, 'masterCalendar.selected');
  const { selectedAdSources } = campaign;
  const timezoneKey = _.get(campaign, 'timezoneSetting.timezoneKey', DEFAULT_TIME_ZONE_KEY);
  const chartDataKey = getChartDataKey({
    range: { ...range, timezone: timezoneKey },
    selectedAdSources
  });
  const chartData = _.get(campaign, ['chartData', chartDataKey], {});
  return chartData;
}

  /**
   * Reformat date string with short year
   * @param {String} dateString - string date with format mm/dd/yyyy, 08/12/2020
   * @returns {String} string date with format mm/dd/yy, 08/12/20
   */
export const formatTimeWithShortYear = (dateString) => {
  return dateString.replace(/^(\d{1,2}\/\d{1,2})(\/\d{1,2})(\d{1,2})$/, '$1/$3');
};

export function calcAvgAdCost(totalCost, totalAds){
  const avgAdCost =
    totalCost === null ? null :
    totalAds && Number(totalCost/totalAds) || 0;
  return avgAdCost;
}

export const shortenIdTree = (parentIdTree) => {
  return parentIdTree.replace(/^(\w{1,})(-.*)(-\w{1,})$/, '$1$3');//'1111-x-xxx-xx-xxxx-2222' => '1111-2222'
}

/**
   * Check if currentObj and nextObj have some different value in props
   * @param {object} currentObj - current object
   * @param {object} nextObj - next object
   * @param {array} propsToCompare - props to compare between current object and next object
   * @returns {bool} true if have at least one value of propsToCompare in current object and next object is different
   * Eg:
   *    currentObj = { x: 1, y: 2, z: 3 };
   *    nextObj = { x: 1, y: 2, z: 4 };
   *    propsToCompare = ['x', 'z'];
   *    return true; (z value is different between currentObj and nextObj)
   */

export const checkObjDiffByProps = (currentObj = {}, nextObj = {}, propsToCompare = []) => {
  for (let i = 0; i < propsToCompare.length; i++) {
    const key = propsToCompare[i];
    if (!_.isEqual(currentObj[key], nextObj[key])) {
      return true;
    }
  }
  return false;
}

export const getEarlistDate = (dateArr = []) => {
  const momentDateArr = dateArr.map(date => moment(date));
  return moment.min(momentDateArr);
}

export const getLatestDate = (dateArr = []) => {
  const momentDateArr = dateArr.map(date => moment(date));
  return moment.max(momentDateArr);
}
export const bytesToSize = (bytes) => {
  var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes == 0) return 'N/A';
  var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  if (i == 0) return bytes + ' ' + sizes[i];
  return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
}

/**
 * Stringify object without quote on properties.
 * @param {Object} object - object to stringify
 * @return {string} - string from object with no quotes on properties
 */

export function stringifyWithNoQuoteOnProperties(obj_from_json) {
  if (Array.isArray(obj_from_json)){
      // stringify array
      let stringObj = obj_from_json
        .map(item => {
          return `${stringifyWithNoQuoteOnProperties(item)}`})
        .join(",");
      return `[${stringObj}]`

  } else if (typeof obj_from_json === "object") {
    // Implements recursive object serialization according to JSON spec
    // but without quotes around the keys.
    let stringObj = Object
        .keys(obj_from_json)
        .map(key => `${key}:${stringifyWithNoQuoteOnProperties(obj_from_json[key])}`)
        .join(",");
    return `{${stringObj}}`;

  } else {
    return JSON.stringify(obj_from_json);
  }
}

export function trimObjectValue(obj){
  const newObj = {};
  Object.entries(obj).forEach(([key, value]) => {
    newObj[key] = typeof value === "string" ? value.trim() : value;
  })
  return newObj;
}

export function getAdvertiserConnectInfo(advertiser) {
  const {
    tracking_sources: trackingSources,
    visualConnectStatus
  } = advertiser;
  const trackingDataSourceName = getTrackingDataSourceName(trackingSources);

  return {
    visualConnectStatus,
    trackingDataSourceName
  };
}

export function getChartDataForDataOfAds(chartData, data, typeCategory, dataType = "visits") {
  // flag to calculate adVisits/ads by duration
  const isCalculatedByDuration = typeCategory === 'creative_duration' && dataType === 'visitOfads';

  const prepChartData = data.reduce((accum, entry) => {
    const category = entry[typeCategory];
    const aired = _.get(entry, 'aired', 0);
    const visits = _.get(entry, 'visits', 0);
    const divider = _.get(entry, dataType, 0);

    if (!accum[category]) {
      accum[category] = {
        ads: aired,
        adVisit: visits,
        [dataType]: isCalculatedByDuration
          ? visits / aired 
          : divider
      };
    } else {
      const currentAds = accum[category].ads + aired;
      const currentVisits = accum[category].adVisit + visits;
      
      accum[category] = {
        ads: currentAds,
        adVisit: currentVisits,
        [dataType]: isCalculatedByDuration
          ? currentVisits / currentAds
          : accum[category][dataType] + divider
      };
    }
    return accum;
  }, {});

  for (let category in prepChartData) {
    // const { ads, visits } = prepChartData[category];
    const divider = _.get(prepChartData, [category, dataType]);
    const dataOfAds = _.isNumber(divider) ? parseFloat(divider.toFixed(2)) : 0;
    chartData[category] = {
      y: category,
      x: dataOfAds
    };
  }

  return chartData;
}

/**
 * @param {dataType: string, categoryType: string} creativeChartSelector chart selector
 * @param {
    *selectedDataType: string,
    *selectedCategoryType: string,
    *data: arrayOf(shape({
      *creative_duration: string,
      *creative_name: string,
      *station_name: arrayOf(string),
      *type_name: string,
      *type_id: number,
      *visitOfads: number,
      *visits: number,
      *aired: number,
      *avgAdCost: number,
      *adUser: number,
    *})),
    *typeOptions: typeOf CREATIVE_TYPE_OPTIONS} creativeProps
 * @param {boolean} isPPTX format data for Component or PPT report
 * @returns data for component/ PPT report
 */

export const formatCreativeChartData = (creativeChartSelector, creativeProps, isPPTX, enabledFlags = []) => {
  //multiple charts of the same type can be added to custom report with different selectors
  // Use defaultChartSelector may broke pdf for old report because we can not get dataType/categoryType from Props anymore.
  // const { dataType: dataTypeInCustomReport, categoryType: categoryTypeInCustomReport } = creativeChartSelector || defaultChartSelector[CREATIVE_CHART_ID];
  const { dataType: dataTypeInCustomReport, categoryType: categoryTypeInCustomReport } = creativeChartSelector || {};
  const { selectedDataType, selectedCategoryType, data, typeOptions } = creativeProps;
  const typeData = dataTypeInCustomReport || _.get(typeOptions, [selectedDataType, 'value'], typeOptions.visitOfads.value);
  const typeCategory = categoryTypeInCustomReport || _.get(typeOptions, [selectedCategoryType, 'value'], typeOptions.creative_name.value);
  const typeVisitOfAds = typeData === typeOptions.visitOfads.value;
  const typeAdVisits = typeData === typeOptions.visits.value;
  const chartData = {};

  if (typeVisitOfAds) {
    getChartDataForDataOfAds(chartData, data, typeCategory, typeData);
  } else {
    data && data.reduce((accum, entry) => {
      const y = entry[typeCategory];
      const x = entry[typeData];
      const ads = entry.aired;
      const cost = entry.totalCost;
      if (x != undefined && y) {
        if (!accum[y]) {
          accum[y] = {
            y,
            x,
            totalAds: ads,
            totalCost: cost
          };
        } else {
          accum[y].x += x;
          accum[y].totalAds += ads;
          accum[y].totalCost += cost;
        }
      }
      return accum;
    }, chartData);
  }

  let result = Object.values(chartData);
  if(typeData === "avgAdCost"){
    result = Object.values(chartData).map(item => {
      const { avgAdCost } = item;
      const element= {
        ...item,
        avgAdCost: _.isNumber(avgAdCost) ? parseFloat(avgAdCost.toFixed(2)) : 'N/A',
      };
        element.x = calcAvgAdCost(element.totalCost,element.totalAds)
        delete element.totalAds;
        delete element.totalCost;
      return element;
    });
  }
  const sortedType = isPPTX ? 'desc' : 'asc';
  const creativeData = _.orderBy(result, ['y'], [sortedType]);
  let formattedValuesForChart = [];
  let formattedLabelsForChart = [];
  creativeData.forEach(item => {
    const { x: value, y: label } = item;
    if (_.isNumber(value)) {
      const formattedValue = typeAdVisits ? parseFloat(value.toFixed(1)) : parseFloat(value.toFixed(2));
      formattedValuesForChart.push(formattedValue);
    }
    formattedLabelsForChart.push(label.trim());
  })
  if (isPPTX) {
    return {
      valuesForChart: formattedValuesForChart,
      labelsForChart: formattedLabelsForChart,
    };
  }
  return creativeData;
}

export const formatCampaignDates = (campaign) => {
  const { start_date: startDate, end_date: endDate } = campaign || {};
  const formatStartDate = moment.parseZone(startDate).format('MM.DD.YY');
  const formatEndDate = moment.parseZone(endDate).format('MM.DD.YY');
  return `Campaign Dates: ${formatStartDate} - ${formatEndDate}`;
}

export const compareDateWithDateRange = ({
  dateRange,
  comparedDate,
  timezoneKey = DEFAULT_TIME_ZONE_KEY,
}) => {
  const { start, end } = dateRange || {};

  if (!start || !end) {
    return {
      isInRange: false,
    };
  }
  const formattedComparedDate = formatDateTimeCommon({
    dateTime: comparedDate,
    timezoneKey
  });
  const formattedStartDate = convertTimezoneDate(start, timezoneKey, true);
  const formattedEndDate = convertTimezoneDate(end, timezoneKey);
  const startTime = new Date(formattedStartDate).getTime();
  const endTime = new Date(formattedEndDate).getTime();
  const dateTime = new Date(formattedComparedDate).getTime();

  return {
    isInRange: startTime <= dateTime && dateTime <= endTime,
    isAfterOrEqual: dateTime >= endTime && dateTime >= startTime,
    isInGA4DateRange: startTime >= dateTime && endTime >= dateTime,
  };
};

export const withInRange = ({
  dateRange,
  comparedDate,
  timezoneKey = DEFAULT_TIME_ZONE_KEY,
}) => {
  const { isInRange } = compareDateWithDateRange({
    dateRange,
    comparedDate,
    timezoneKey,
  });
  return isInRange;
};

export const dateFormat = {
  dayPart: 'MM/DD',
  web: 'MM/DD/YYYY',
}

export const weekDayFormat = {
  dayPart: 'MM/DD',
  web: 'MM/DD/YY',
}

export function formatCutOverDate({
  cutOverDate,
  dateTimeFormat = dateFormat.web,
  timezoneKey,
}) {
  if (!cutOverDate || !moment(cutOverDate).isValid()) {
    return '';
  }
  return moment.tz(cutOverDate, timezoneKey).format(dateTimeFormat);
}

export function formatCutOverDateByTimeRange({
  cutOverDate,
  timeRange,
  dateFormatType = 'web',
  campaignDateRange,
  timezoneKey = DEFAULT_TIME_ZONE_KEY,
}) {
  if (!cutOverDate || !moment(cutOverDate).isValid()) {
    return;
  }
  let formattedCutOverDate;
  if (timeRange === 'weekly') {
    const { start, end } = campaignDateRange;
    let startDay = moment(cutOverDate).startOf('week');
    let endDay = moment(cutOverDate).endOf('week');
    if (new Date(start).getTime() > new Date(startDay).getTime()) {
      startDay = start;
    }
    if (new Date(end).getTime() < new Date(endDay).getTime()) {
      endDay = end;
    }
    startDay = moment.tz(startDay, timezoneKey).format(weekDayFormat[dateFormatType]);
    endDay = moment.tz(endDay, timezoneKey).format(weekDayFormat[dateFormatType]);
    formattedCutOverDate = `${startDay} - ${endDay}`;
  } else if (timeRange === 'monthly') {
    formattedCutOverDate = moment.tz(cutOverDate, timezoneKey).format('MMM YYYY');
  } else {
    formattedCutOverDate = moment.tz(cutOverDate, timezoneKey).format(dateFormat[dateFormatType]);
  }
  return formattedCutOverDate;
}

export function resetNewAdUserChartSelector(chartSelector) {
  if (_.isEmpty(chartSelector)) {
    return chartSelector;
  }
  const newChartSelector = _.cloneDeep(chartSelector);
  for (const [chartId, currentChartSelector] of Object.entries(newChartSelector)) {
    switch (Number(chartId)) {
      case ADTYPE_CHART_ID:
        {
          mutateChartSelectorByChartKey(currentChartSelector, chartId, ADTYPE_KEY);
          break;
        }
      case STATION_CHART_ID:
        {
          mutateChartSelectorByChartKey(currentChartSelector, chartId, STATION_TYPE_KEY);
          break;
        }
      case ADVISIT_CHART_ID:
        {
          mutateChartSelectorByChartKey(currentChartSelector, chartId, DATA_TYPE);
          break;
        }
      default:
        {
          mutateChartSelectorByChartKey(currentChartSelector, chartId, TYPE_KEY);

        }
    }
  }
  return newChartSelector
}

function mutateChartSelectorByChartKey(chartSelectorInfo, chartId, chartKey) {
  const defaultChartSelectorValue = _.cloneDeep(_.get(defaultChartSelector, [chartId, chartKey]));
  if (Array.isArray(chartSelectorInfo)){
    chartSelectorInfo.forEach(item => {
      if (item[chartKey] === newAdUsersKey) {
        item[chartKey] = defaultChartSelectorValue;
      }
    })
  } else {
    if (chartSelectorInfo[chartKey] === newAdUsersKey) {
      chartSelectorInfo[chartKey] = defaultChartSelectorValue;
    }
  }
}

export function resetNewAdUserTableSortSelector(tableSortSelector) { 
  if (_.isEmpty(tableSortSelector)) {
    return tableSortSelector;
  }
  const newTableSortSelector = _.cloneDeep(tableSortSelector);
  for (const [chartId, sortObj] of Object.entries(newTableSortSelector)) {
    Object.values(sortObj).forEach(item => {
      if (!item) {
        return
      }
      if (item.sortKey === 'newAdUsers' || item.sortKey === 'newAdUsersPerAds' || item.sortKey === 'newAdUsersOfAds') {
        newTableSortSelector[chartId] = {};
      }
    }) 
  }
  return newTableSortSelector;
}


export const dateToDatePixel = (date, format = 'MMM D', isUseFormat = false) => {
  if (_.isNil(date)) {
    return '--';
  }
  const utcDate = date.endsWith('Z') ? date : `${date}Z`
  const dateObject = new Date(utcDate);
  const localDate = moment.utc(dateObject).local();
  const today = moment();
  const yesterday = moment().subtract(1, 'days');
  if (localDate.isSame(today, 'day')) {
    const localDateToday = localDate && localDate.format(format);
    return `Today, ${localDateToday}`;

  }
  
  if (localDate.isSame(yesterday, 'day')) {
    const localDateToday = localDate && localDate.format(format);
    return `Yesterday, ${localDateToday}`;
  }

  return localDate.format(isUseFormat ? format : 'MMM D, YYYY');
};

export const formatString = (string) => {
    return string || '---'
}

export function truncateStringWithRegex(str, limit) {
  if(!str) {
    return '';
  } 

  return str.length > limit
    ? str.slice(0, limit).replace(/[\s.,!?:;]*$/, "...")
    : str;
}

export const generateDateFormats = (dateInput, enablePlayoutVer2 = false) => {
  const date = new Date(dateInput);

  if (isNaN(date)) {
    throw new Error('Invalid date input');
  }

  const yyyy = date.getFullYear();
  const yy = String(yyyy).slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const m = date.getMonth() + 1;
  const dd = String(date.getDate()).padStart(2, '0');
  const d = date.getDate();

  const originalFormats = [
    `${yyyy}/${mm}/${dd}`,
    `${yyyy}-${mm}-${dd}`,
    `${m}/${d}/${yy}`,
    `${m}-${d}-${yy}`,
    `${m}/${d}/${yyyy}`,
    `${m}-${d}-${yyyy}`,
  ];

  const supportedVer2Formats = [
    `${yyyy}/${mm}/${dd}`,
    `${yyyy}-${mm}-${dd}`,
    `${m}/${d}/${yy}`,
    `${m}-${d}-${yy}`,
    `${m}/${d}/${yyyy}`,
    `${m}-${d}-${yyyy}`,
    `${mm}-${dd}-${yyyy}`,
    `${yyyy}-${m}-${d}`,
    `${mm}/${dd}/${yyyy}`,
    `${mm}.${dd}.${yyyy}`,
    `${dd}.${mm}.${yyyy}`,
    `${m}.${d}.${yyyy}`,
    `${d}.${m}.${yyyy}`,
    `${dd}/${mm}/${yyyy}`,
    `${d}/${m}/${yyyy}`,
    `${yyyy}.${m}.${d}`,
    `${yyyy}.${mm}.${dd}`,
    `${dd}-${mm}-${yyyy}`,
    `${d}-${m}-${yyyy}`,
  ]

  return enablePlayoutVer2 ? supportedVer2Formats : originalFormats;
}
